import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../context/ThemeContext'
import { authApi, calendarApi } from '../api/client'
import { 
  HiUser, 
  HiBell, 
  HiColorSwatch, 
  HiSave, 
  HiCheck,
  HiBriefcase,
  HiOfficeBuilding,
  HiMail,
  HiIdentification,
  HiClock,
  HiShieldCheck,
  HiCog,
  HiPhotograph,
  HiGlobe,
  HiMoon,
  HiSun,
  HiRefresh,
  HiCalendar,
  HiDownload,
  HiExternalLink,
  HiClipboardList,
  HiPhone,
  HiLocationMarker
} from 'react-icons/hi'
import { format } from 'date-fns'

export default function SettingsPage() {
  const { user, refreshUser } = useAuth()
  const { theme: currentTheme, setTheme: applyTheme } = useTheme()
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState(null)
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    emailReminders: true,
    emailAssignments: true,
    emailMentions: true,
    teamsNotifications: true,
    dailyDigest: false,
    weeklyReport: true,
  })

  // Appearance settings
  const [appearance, setAppearance] = useState({
    theme: currentTheme || 'light',
    compactMode: false,
    showAvatars: true,
  })

  useEffect(() => {
    if (user?.settings) {
      if (user.settings.notifications) {
        setNotifications(prev => ({ ...prev, ...user.settings.notifications }))
      }
      if (user.settings.appearance) {
        setAppearance(prev => ({ ...prev, ...user.settings.appearance }))
      }
    }
  }, [user])

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    
    try {
      await authApi.updateMe({ display_name: displayName })
      await refreshUser()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to update profile:', err)
      alert('Failed to save changes. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    try {
      await authApi.updateMe({ 
        settings: {
          ...user?.settings,
          notifications,
          appearance,
        }
      })
      await refreshUser()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to update settings:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleCalendarDownload = async (type) => {
    try {
      let response, filename
      if (type === 'my-tasks') {
        response = await calendarApi.downloadMyTasks()
        filename = 'my-tasks.ics'
      } else if (type === 'reminders') {
        response = await calendarApi.downloadReminders()
        filename = 'reminders.ics'
      }
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Calendar export failed:', err)
    }
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: HiUser },
    { id: 'account', label: 'Account', icon: HiShieldCheck },
    { id: 'notifications', label: 'Notifications', icon: HiBell },
    { id: 'calendar', label: 'Calendar', icon: HiCalendar },
    { id: 'appearance', label: 'Appearance', icon: HiColorSwatch },
  ]

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">Settings</h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">Manage your account and preferences</p>
      </div>

      <div className="flex gap-8">
        {/* Sidebar Tabs */}
        <nav className="w-48 shrink-0">
          <ul className="space-y-1">
            {tabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border-l-2 border-brand-600'
                      : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-800 dark:hover:text-surface-200'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <div className="flex-1 space-y-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              {/* Profile Card */}
              <div className="card p-6">
                <div className="flex items-start gap-6">
                  {/* Avatar */}
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                      {user?.display_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <button className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                      <HiPhotograph className="w-6 h-6 text-white" />
                    </button>
                  </div>

                  {/* Basic Info */}
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-surface-800 dark:text-surface-200">{user?.display_name}</h2>
                    <p className="text-surface-500 dark:text-surface-400">{user?.email}</p>
                    
                    <div className="flex items-center gap-4 mt-3">
                      {user?.job_title && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-surface-600 dark:text-surface-400">
                          <HiBriefcase className="w-4 h-4 text-surface-400 dark:text-surface-500" />
                          {user.job_title}
                        </span>
                      )}
                      {user?.entra_department && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-surface-600 dark:text-surface-400">
                          <HiOfficeBuilding className="w-4 h-4 text-surface-400 dark:text-surface-500" />
                          {user.entra_department}
                        </span>
                      )}
                      {user?.office_location && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-surface-600 dark:text-surface-400">
                          <HiLocationMarker className="w-4 h-4 text-surface-400 dark:text-surface-500" />
                          {user.office_location}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${
                        user?.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                        user?.role === 'manager' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                        'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-400'
                      }`}>
                        {user?.role || 'Member'}
                      </span>
                      <span className="text-xs text-surface-400 dark:text-surface-500">•</span>
                      <span className="text-xs text-surface-500 dark:text-surface-400">
                        {user?.organization?.name || 'No Organization'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Edit Profile */}
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-4 flex items-center gap-2">
                  <HiCog className="w-5 h-5 text-brand-500" />
                  Edit Profile
                </h3>

                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Display Name</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="input"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="label">Email</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="input bg-surface-50 dark:bg-surface-800 text-surface-500 dark:text-surface-400"
                      />
                      <p className="text-xs text-surface-400 dark:text-surface-500 mt-1">Managed by your organization</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn btn-primary"
                    >
                      {saving ? (
                        'Saving...'
                      ) : saved ? (
                        <>
                          <HiCheck className="w-4 h-4 mr-2" />
                          Saved
                        </>
                      ) : (
                        <>
                          <HiSave className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <>
              {/* Entra ID Information */}
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-4 flex items-center gap-2">
                  <HiShieldCheck className="w-5 h-5 text-brand-500" />
                  Microsoft Entra ID Information
                </h3>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-surface-500 dark:text-surface-400">
                    This information is synced from your organization's Microsoft Entra ID (Azure AD).
                  </p>
                  <button
                    onClick={async () => {
                      setSyncing(true)
                      setSyncMessage(null)
                      try {
                        const res = await authApi.syncProfile()
                        setSyncMessage({ type: 'success', text: res.data.message })
                        await refreshUser()
                      } catch (e) {
                        setSyncMessage({ type: 'error', text: e.response?.data?.detail || 'Sync failed' })
                      } finally {
                        setSyncing(false)
                      }
                    }}
                    disabled={syncing}
                    className="btn btn-sm flex items-center gap-1.5 text-sm"
                  >
                    <HiRefresh className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync from Entra ID'}
                  </button>
                </div>

                {syncMessage && (
                  <div className={`text-sm px-3 py-2 rounded-lg mb-4 ${
                    syncMessage.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                    {syncMessage.text}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <InfoRow 
                    icon={HiMail} 
                    label="Email Address" 
                    value={user?.email} 
                  />
                  <InfoRow 
                    icon={HiUser} 
                    label="Display Name" 
                    value={user?.display_name} 
                  />
                  <InfoRow 
                    icon={HiBriefcase} 
                    label="Job Title" 
                    value={user?.job_title || 'Not set'} 
                    muted={!user?.job_title}
                  />
                  <InfoRow 
                    icon={HiOfficeBuilding} 
                    label="Department" 
                    value={user?.entra_department || 'Not set'} 
                    muted={!user?.entra_department}
                  />
                  <InfoRow 
                    icon={HiLocationMarker} 
                    label="Office Location" 
                    value={user?.office_location || 'Not set'} 
                    muted={!user?.office_location}
                  />
                  <InfoRow 
                    icon={HiPhone} 
                    label="Phone" 
                    value={user?.mobile_phone || 'Not set'} 
                    muted={!user?.mobile_phone}
                  />
                  <InfoRow 
                    icon={HiGlobe} 
                    label="Organization" 
                    value={user?.organization?.name || user?.company_name || 'Not set'} 
                    muted={!user?.organization && !user?.company_name}
                  />
                  <InfoRow 
                    icon={HiIdentification} 
                    label="Role" 
                    value={user?.role} 
                    capitalize
                  />
                </div>
              </div>

              {/* Account Activity */}
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-4 flex items-center gap-2">
                  <HiClock className="w-5 h-5 text-brand-500" />
                  Account Activity
                </h3>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-surface-100 dark:border-surface-700">
                    <div>
                      <p className="text-sm font-medium text-surface-700 dark:text-surface-300">Account Created</p>
                      <p className="text-xs text-surface-500 dark:text-surface-400">When your account was first created</p>
                    </div>
                    <p className="text-sm text-surface-600 dark:text-surface-400">
                      {user?.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </p>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-surface-100 dark:border-surface-700">
                    <div>
                      <p className="text-sm font-medium text-surface-700 dark:text-surface-300">Last Active</p>
                      <p className="text-xs text-surface-500 dark:text-surface-400">Your last recorded activity</p>
                    </div>
                    <p className="text-sm text-surface-600 dark:text-surface-400">
                      {user?.last_active 
                        ? format(new Date(user.last_active), 'MMM d, yyyy h:mm a') 
                        : 'Never'}
                    </p>
                  </div>

                  <div className="flex justify-between items-center py-3">
                    <div>
                      <p className="text-sm font-medium text-surface-700 dark:text-surface-300">Last Synced from Entra ID</p>
                      <p className="text-xs text-surface-500 dark:text-surface-400">When your profile was last updated from Microsoft</p>
                    </div>
                    <p className="text-sm text-surface-600 dark:text-surface-400">
                      {user?.last_synced_at 
                        ? format(new Date(user.last_synced_at), 'MMM d, yyyy h:mm a') 
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {/* User ID */}
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-4">Technical Information</h3>
                <div className="bg-surface-50 dark:bg-surface-800 rounded-lg p-4">
                  <p className="text-xs text-surface-500 dark:text-surface-400 mb-1">User ID</p>
                  <code className="text-sm font-mono text-surface-700 dark:text-surface-300">{user?.id}</code>
                </div>
              </div>
            </>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="card p-6">
              <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-6 flex items-center gap-2">
                <HiBell className="w-5 h-5 text-brand-500" />
                Notification Preferences
              </h3>

              <div className="space-y-6">
                {/* Email Notifications */}
                <div>
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Email Notifications</h4>
                  <div className="space-y-3">
                    <ToggleOption
                      label="Reminders"
                      description="Get notified when a reminder is due"
                      checked={notifications.emailReminders}
                      onChange={(v) => setNotifications(p => ({ ...p, emailReminders: v }))}
                    />
                    <ToggleOption
                      label="Task Assignments"
                      description="When you're assigned to a new task"
                      checked={notifications.emailAssignments}
                      onChange={(v) => setNotifications(p => ({ ...p, emailAssignments: v }))}
                    />
                    <ToggleOption
                      label="Mentions"
                      description="When someone mentions you in a comment"
                      checked={notifications.emailMentions}
                      onChange={(v) => setNotifications(p => ({ ...p, emailMentions: v }))}
                    />
                  </div>
                </div>

                {/* Microsoft Teams */}
                <div className="pt-4 border-t border-surface-200 dark:border-surface-700">
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Microsoft Teams</h4>
                  <div className="space-y-3">
                    <ToggleOption
                      label="Teams Notifications"
                      description="Send notifications to Microsoft Teams"
                      checked={notifications.teamsNotifications}
                      onChange={(v) => setNotifications(p => ({ ...p, teamsNotifications: v }))}
                    />
                  </div>
                </div>

                {/* Digests */}
                <div className="pt-4 border-t border-surface-200 dark:border-surface-700">
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Digests & Reports</h4>
                  <div className="space-y-3">
                    <ToggleOption
                      label="Daily Digest"
                      description="Receive a daily summary of activity"
                      checked={notifications.dailyDigest}
                      onChange={(v) => setNotifications(p => ({ ...p, dailyDigest: v }))}
                    />
                    <ToggleOption
                      label="Weekly Report"
                      description="Get a weekly overview sent every Monday"
                      checked={notifications.weeklyReport}
                      onChange={(v) => setNotifications(p => ({ ...p, weeklyReport: v }))}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button onClick={handleSaveSettings} className="btn btn-primary">
                    {saving ? 'Saving...' : saved ? (
                      <><HiCheck className="w-4 h-4 mr-2" /> Saved</>
                    ) : (
                      <><HiSave className="w-4 h-4 mr-2" /> Save Preferences</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <>
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-2 flex items-center gap-2">
                  <HiCalendar className="w-5 h-5 text-brand-500" />
                  Calendar Sync
                </h3>
                <p className="text-sm text-surface-500 dark:text-surface-400 mb-6">
                  Export your tasks and reminders to any calendar app — Outlook, Google Calendar, Apple Calendar, or any app that supports .ics files.
                </p>

                <div className="space-y-4">
                  {/* My Tasks */}
                  <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-700/50 rounded-xl border border-surface-200 dark:border-surface-600">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-100 dark:bg-brand-900/40 rounded-lg flex items-center justify-center">
                        <HiClipboardList className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-800 dark:text-surface-200">My Tasks</p>
                        <p className="text-xs text-surface-500 dark:text-surface-400">All tasks assigned to you with due dates</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCalendarDownload('my-tasks')}
                      className="btn btn-sm flex items-center gap-1.5"
                    >
                      <HiDownload className="w-4 h-4" />
                      Download .ics
                    </button>
                  </div>

                  {/* Reminders */}
                  <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-700/50 rounded-xl border border-surface-200 dark:border-surface-600">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center">
                        <HiBell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-800 dark:text-surface-200">Reminders</p>
                        <p className="text-xs text-surface-500 dark:text-surface-400">All your pending reminders as calendar events</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCalendarDownload('reminders')}
                      className="btn btn-sm flex items-center gap-1.5"
                    >
                      <HiDownload className="w-4 h-4" />
                      Download .ics
                    </button>
                  </div>
                </div>
              </div>

              {/* How to use */}
              <div className="card p-6">
                <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-4 flex items-center gap-2">
                  <HiExternalLink className="w-5 h-5 text-brand-500" />
                  How to Import
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <p className="text-sm text-surface-600 dark:text-surface-400">Click "Download .ics" for the calendar you want to sync.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <p className="text-sm text-surface-600 dark:text-surface-400">Open the downloaded .ics file — your default calendar app will prompt you to import.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <p className="text-sm text-surface-600 dark:text-surface-400">Tasks appear as calendar events on their due dates with priority and project info.</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Tip:</strong> You can also export project-specific calendars from the AI chat panel's menu (&#8942;) when viewing a project.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="card p-6">
              <h3 className="font-semibold text-surface-800 dark:text-surface-200 mb-6 flex items-center gap-2">
                <HiColorSwatch className="w-5 h-5 text-brand-500" />
                Appearance Settings
              </h3>

              <div className="space-y-6">
                {/* Theme */}
                <div>
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Theme</h4>
                  <div className="flex gap-3">
                    {[
                      { id: 'light', label: 'Light', icon: HiSun },
                      { id: 'dark', label: 'Dark', icon: HiMoon },
                      { id: 'system', label: 'System', icon: HiCog },
                    ].map(theme => (
                      <button
                        key={theme.id}
                        onClick={() => {
                          setAppearance(p => ({ ...p, theme: theme.id }))
                          applyTheme(theme.id)
                        }}
                        className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          appearance.theme === theme.id
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-surface-200 dark:border-surface-600 hover:border-surface-300 dark:hover:border-surface-500'
                        }`}
                      >
                        <theme.icon className={`w-6 h-6 ${
                          appearance.theme === theme.id ? 'text-brand-600 dark:text-brand-400' : 'text-surface-500'
                        }`} />
                        <span className={`text-sm font-medium ${
                          appearance.theme === theme.id ? 'text-brand-700 dark:text-brand-300' : 'text-surface-600 dark:text-surface-400'
                        }`}>
                          {theme.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-surface-500 dark:text-surface-400 mt-2">
                    Choose your preferred theme. System will match your OS settings.
                  </p>
                </div>

                {/* Display Options */}
                <div className="pt-4 border-t border-surface-200 dark:border-surface-700">
                  <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">Display Options</h4>
                  <div className="space-y-3">
                    <ToggleOption
                      label="Compact Mode"
                      description="Reduce padding and spacing for denser layouts"
                      checked={appearance.compactMode}
                      onChange={(v) => setAppearance(p => ({ ...p, compactMode: v }))}
                    />
                    <ToggleOption
                      label="Show Avatars"
                      description="Display user avatars in lists and cards"
                      checked={appearance.showAvatars}
                      onChange={(v) => setAppearance(p => ({ ...p, showAvatars: v }))}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button onClick={handleSaveSettings} className="btn btn-primary">
                    {saving ? 'Saving...' : saved ? (
                      <><HiCheck className="w-4 h-4 mr-2" /> Saved</>
                    ) : (
                      <><HiSave className="w-4 h-4 mr-2" /> Save Preferences</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function InfoRow({ icon: Icon, label, value, muted = false, capitalize = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-surface-100 dark:bg-surface-700 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-surface-500 dark:text-surface-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-surface-500 dark:text-surface-400">{label}</p>
        <p className={`text-sm font-medium truncate ${
          muted ? 'text-surface-400 dark:text-surface-500 italic' : 'text-surface-800 dark:text-surface-200'
        } ${capitalize ? 'capitalize' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  )
}

function ToggleOption({ label, description, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-2 cursor-pointer group">
      <div>
        <p className="text-sm font-medium text-surface-700 dark:text-surface-300 group-hover:text-surface-800 dark:group-hover:text-surface-200">{label}</p>
        <p className="text-xs text-surface-500 dark:text-surface-400">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-brand-600' : 'bg-surface-300 dark:bg-surface-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white dark:bg-surface-200 rounded-full shadow-sm transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  )
}
